/**
 * UploadsDrawer.tsx
 * Drawer pour l'upload et la gestion des images
 * Extrait de ModernSidebar.tsx
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppStore } from '../../../../services/StateService';
import { createDefaultShape } from '../../../../types/ShapeTypes';
import { AssetsLibrary, type AssetItem } from '../../../../AssetsLibrary';
import { logger } from '../../../../utils/ProfessionalLogger';
import { ConfirmModal } from '../../common/ConfirmModal';

interface UploadsDrawerProps {
  onClose: () => void;
}

export const UploadsDrawer: React.FC<UploadsDrawerProps> = ({ onClose }) => {
  const [uploadedImages, setUploadedImages] = useState<AssetItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const addShape = useAppStore(state => state.addShape);
  const assetsLibrary = useMemo(() => new AssetsLibrary(), []);
  // A07 : asset en attente de suppression (ouvre la ConfirmModal).
  const [deleteAsset, setDeleteAsset] = useState<AssetItem | null>(null);

  // Charger les images depuis AssetsLibrary au montage
  useEffect(() => {
    const loadAssets = () => {
      const allAssets = assetsLibrary.getAssetsByType('image');
      setUploadedImages(allAssets);
    };
    loadAssets();
  }, [assetsLibrary]);

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;

      setIsLoading(true);
      try {
        const uploadPromises = Array.from(files)
          .filter(file => file.type.startsWith('image/'))
          .map(async file => {
            const dataURL = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result;
                if (typeof result === 'string') {
                  resolve(result);
                } else {
                  reject(new Error('Impossible de lire le fichier comme DataURL'));
                }
              };
              reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'));
              reader.readAsDataURL(file);
            });

            const assetId = await assetsLibrary.addAsset(file.name, dataURL, 'image', []);
            return assetsLibrary.getAsset(assetId);
          });

        const newAssets = (await Promise.all(uploadPromises)).filter(
          (asset): asset is AssetItem => asset !== null
        );

        setUploadedImages(prev => [...prev, ...newAssets]);
      } catch (error) {
        logger.error("Erreur lors de l'upload des images:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [assetsLibrary]
  );

  const handleImageClick = useCallback(
    (asset: AssetItem) => {
      const img = new window.Image();
      img.onload = () => {
        const shape = createDefaultShape('photoSpace', img.width, img.height);
        shape.src = asset.dataURL;
        addShape(shape);
        assetsLibrary.markAssetAsUsed(asset.id).catch(error => {
          logger.error("Erreur lors de la mise à jour de l'asset:", error);
        });
        onClose();
      };
      img.src = asset.dataURL;
    },
    [addShape, onClose, assetsLibrary]
  );

  // A07 : ouvre la confirmation au lieu de window.confirm.
  const handleDeleteAsset = useCallback((asset: AssetItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteAsset(asset);
  }, []);

  const confirmDeleteAsset = useCallback(async () => {
    if (!deleteAsset) return;
    const assetId = deleteAsset.id;
    try {
      const removed = await assetsLibrary.removeAsset(assetId);
      if (removed) {
        setUploadedImages(prev => prev.filter(asset => asset.id !== assetId));
      }
    } catch (error) {
      logger.error("Erreur lors de la suppression de l'asset:", error);
    } finally {
      setDeleteAsset(null);
    }
  }, [deleteAsset, assetsLibrary]);

  // A07 : éléments du canvas utilisant cette image (champ src = dataURL).
  const deleteUsageCount = deleteAsset
    ? useAppStore.getState().shapes.filter(s => s.src === deleteAsset.dataURL).length
    : 0;

  return (
    <>
      <div className="drawer-header">
        <h3 className="drawer-title">Mes images</h3>
        <button className="drawer-close-btn" onClick={onClose} aria-label="Fermer">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div className="drawer-content">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--spacing-lg)',
              border: '2px dashed var(--color-border)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              transition: 'all var(--transition-fast)',
              backgroundColor: 'var(--color-bg-secondary)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--color-primary)';
              e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
            }}
          >
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'var(--spacing-sm)',
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span
                style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}
              >
                Cliquez pour importer
              </span>
            </div>
          </label>

          {isLoading ? (
            <div
              style={{
                textAlign: 'center',
                padding: 'var(--spacing-xl)',
                color: 'var(--color-text-secondary)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              Chargement...
            </div>
          ) : uploadedImages.length > 0 ? (
            <div className="drawer-grid">
              {uploadedImages.map(asset => (
                <div
                  key={asset.id}
                  className="drawer-item"
                  onClick={() => handleImageClick(asset)}
                  style={{ position: 'relative' }}
                >
                  <div className="drawer-item-preview" style={{ padding: 0, overflow: 'hidden' }}>
                    <img
                      src={asset.thumbnail || asset.dataURL}
                      alt={asset.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                  <button
                    onClick={e => handleDeleteAsset(asset, e)}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      background: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      padding: 0,
                    }}
                    title="Supprimer"
                    aria-label="Supprimer cette image"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                textAlign: 'center',
                padding: 'var(--spacing-xl)',
                color: 'var(--color-text-secondary)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              Aucune image importée
            </div>
          )}
        </div>
      </div>

      {/* A07 : confirmation de suppression avec avertissement d'usage */}
      <ConfirmModal
        isOpen={!!deleteAsset}
        danger
        title="Supprimer cette image ?"
        message={
          deleteUsageCount > 0 ? (
            <>
              Cette image est utilisée par <strong>{deleteUsageCount}</strong> élément
              {deleteUsageCount > 1 ? 's' : ''} du canvas. Ces éléments <strong>conservent</strong>{' '}
              leur image — seule l'entrée de la bibliothèque sera retirée.
            </>
          ) : (
            "Cette image sera retirée de la bibliothèque. Cette action n'affecte pas les éléments déjà posés sur le canvas."
          )
        }
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={confirmDeleteAsset}
        onClose={() => setDeleteAsset(null)}
      />
    </>
  );
};
